// src/pages/Dashboard/Dashboard.js
import React, { useState, useEffect, Fragment } from 'react';
import { toast } from 'react-toastify';
import { dashboardService, inventoryService } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import { Tab } from '@headlessui/react';
import {
    BarChart,
    Bar,
    LineChart,
    Line,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    ResponsiveContainer,
} from 'recharts';
import {
    ExclamationCircleIcon,
    TruckIcon,
    ShoppingCartIcon,
    CubeIcon,
    TableCellsIcon,
    DocumentIcon,
    ExclamationTriangleIcon,
    CheckCircleIcon,
    CheckIcon,
} from '@heroicons/react/24/outline';

// Import the tab panel components
import AIInsights from '../../components/ai/AIInsights';
import FinancialSummary from '../../components/dashboard/FinancialSummary';
import LowStockPartsPanel from './LowStockPartsPanel';
import UnfulfillableProductsPanel from './UnfulfillableProductsPanel';
import UnfulfillableOrdersPanel from './UnfulfillableOrdersPanel';
import FulfillableProductsPanel from './FulfillableProductsPanel';
import FulfillableOrdersPanel from './FulfillableOrdersPanel';
import {Link} from "react-router-dom";

const Dashboard = () => {
    const { currentUser } = useAuth();
    const [dashboardData, setDashboardData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [selectedTab, setSelectedTab] = useState(0);

    useEffect(() => {
        console.log('On Dashboard page')
        const fetchDashboardData = async () => {
            try {
                setLoading(true);
                const response = await dashboardService.getDashboardData();
                setDashboardData(response.data);
                setError(null);
            } catch (err) {
                console.error('Error fetching dashboard data:', err);
                setError('Failed to load dashboard data. Please try again later.');
                toast.error('Failed to load dashboard data');
            } finally {
                setLoading(false);
            }
        };

        fetchDashboardData();
    }, []);

    const generateCutSheet = async () => {
        try {
            const response = await inventoryService.generateCutSheet();

            // Create a download link for the CSV
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `airpac_cutsheet_${new Date().toISOString().split('T')[0]}.csv`);
            document.body.appendChild(link);
            link.click();

            toast.success('Cut sheet generated successfully');
        } catch (error) {
            console.error('Error generating cut sheet:', error);
            toast.error('Failed to generate cut sheet');
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-full">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-500"></div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="bg-red-100 border-l-4 border-red-500 text-red-700 p-4 rounded shadow-md">
                <p className="font-bold">Error</p>
                <p>{error}</p>
            </div>
        );
    }

    // Calculate fulfillable stats for the stats card
    const fulfillableOrders = dashboardData?.low_stock_warnings?.fulfillable_orders?.length || 0;
    const totalOrders = (dashboardData?.low_stock_warnings?.summary?.total_unfulfillable_orders || 0) + fulfillableOrders;
    const fulfillablePercentage = totalOrders > 0
                                  ? Math.round((fulfillableOrders / totalOrders) * 100)
                                  : 100;

    const productionData = dashboardData?.production_statistics?.monthly_production || [];
    const maxProduction = Math.max(...productionData.map(item => item.total || 0));
    const yAxisDomain = [0, Math.ceil(maxProduction * 1.2)]; // 20% padding

    return (
        <div className="space-y-6">
            <div className="flex justify-between items-center">
                <h1 className="text-2xl font-semibold text-gray-800">Dashboard</h1>
                <div className="flex items-center justify-center h-full gap-1">
                    <Link
                        to={`/production/new`}
                        className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                    >
                        New Batch
                    </Link>
                    <Link
                        to={`/orders/new`}
                        className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                    >
                        New Order
                    </Link>
                    <button
                        onClick={generateCutSheet}
                        className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                    >
                        Generate Cut Sheet
                    </button>
                </div>

            </div>

            {/* Stats Cards */}
            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-6">
                {/* Total Orders Card */}
                <div className="bg-white overflow-hidden shadow rounded-lg">
                    <div className="p-5">
                        <div className="flex items-center">
                            <div className="flex-shrink-0">
                                <ShoppingCartIcon className="h-6 w-6 text-gray-400" />
                            </div>
                            <div className="ml-5 w-0 flex-1">
                                <dl>
                                    <dt className="text-sm font-medium text-gray-500 truncate">Total Orders</dt>
                                    <dd>
                                        <div className="text-lg font-medium text-gray-900">
                                            {dashboardData?.order_statistics?.total_orders || 0}
                                        </div>
                                    </dd>
                                </dl>
                            </div>
                        </div>
                    </div>
                    <div className="bg-gray-50 px-5 py-3">
                        <div className="text-sm">
                            <a href="/orders?status=pending" className="font-medium text-primary-600 hover:text-primary-500">
                                View all
                            </a>
                        </div>
                    </div>
                </div>

                {/* Pending Orders Card */}
                <div className="bg-white overflow-hidden shadow rounded-lg">
                    <div className="p-5">
                        <div className="flex items-center">
                            <div className="flex-shrink-0">
                                <ShoppingCartIcon className="h-6 w-6 text-gray-400" />
                            </div>
                            <div className="ml-5 w-0 flex-1">
                                <dl>
                                    <dt className="text-sm font-medium text-gray-500 truncate">Pending Orders</dt>
                                    <dd>
                                        <div className="text-lg font-medium text-gray-900">
                                            {dashboardData?.order_statistics?.pending_orders || 0}
                                        </div>
                                    </dd>
                                </dl>
                            </div>
                        </div>
                    </div>
                    <div className="bg-gray-50 px-5 py-3">
                        <div className="text-sm">
                            <a href="/orders?status=pending" className="font-medium text-primary-600 hover:text-primary-500">
                                View all
                            </a>
                        </div>
                    </div>
                </div>

                {/* Low Stock Items Card */}
                <div className="bg-white overflow-hidden shadow rounded-lg">
                    <div className="p-5">
                        <div className="flex items-center">
                            <div className="flex-shrink-0">
                                <ExclamationCircleIcon className="h-6 w-6 text-yellow-400" />
                            </div>
                            <div className="ml-5 w-0 flex-1">
                                <dl>
                                    <dt className="text-sm font-medium text-gray-500 truncate">Low Stock Items</dt>
                                    <dd>
                                        <div className="text-lg font-medium text-gray-900">
                                            {dashboardData?.inventory_status?.parts_below_threshold || 0}
                                        </div>
                                    </dd>
                                </dl>
                            </div>
                        </div>
                    </div>
                    <div className="bg-gray-50 px-5 py-3">
                        <div className="text-sm">
                            <a href="/inventory" className="font-medium text-primary-600 hover:text-primary-500">
                                View all
                            </a>
                        </div>
                    </div>
                </div>

                {/* Unfulfillable Orders Card */}
                <div className="bg-white overflow-hidden shadow rounded-lg">
                    <div className="p-5">
                        <div className="flex items-center">
                            <div className="flex-shrink-0">
                                <ExclamationTriangleIcon className="h-6 w-6 text-red-400" />
                            </div>
                            <div className="ml-5 w-0 flex-1">
                                <dl>
                                    <dt className="text-sm font-medium text-gray-500 truncate">Unfulfillable Orders</dt>
                                    <dd>
                                        <div className="text-lg font-medium text-gray-900">
                                            {dashboardData?.low_stock_warnings?.summary?.total_unfulfillable_orders || 0}
                                        </div>
                                    </dd>
                                </dl>
                            </div>
                        </div>
                    </div>
                    <div className="bg-gray-50 px-5 py-3">
                        <div className="text-sm">
                            <a href="#" onClick={(e) => {e.preventDefault(); setSelectedTab(2);}} className="font-medium text-primary-600 hover:text-primary-500">
                                View details
                            </a>
                        </div>
                    </div>
                </div>

                {/* Fulfillable Orders Card */}
                <div className="bg-white overflow-hidden shadow rounded-lg">
                    <div className="p-5">
                        <div className="flex items-center">
                            <div className="flex-shrink-0">
                                <CheckCircleIcon className="h-6 w-6 text-green-400" />
                            </div>
                            <div className="ml-5 w-0 flex-1">
                                <dl>
                                    <dt className="text-sm font-medium text-gray-500 truncate">Fulfillable Orders</dt>
                                    <dd>
                                        <div className="text-lg font-medium text-gray-900">
                                            {dashboardData?.low_stock_warnings?.fulfillable_orders?.length || 0}
                                        </div>
                                    </dd>
                                </dl>
                            </div>
                        </div>
                    </div>
                    <div className="bg-gray-50 px-5 py-3">
                        <div className="text-sm">
                            <a href="#" onClick={(e) => {e.preventDefault(); setSelectedTab(4);}} className="font-medium text-primary-600 hover:text-primary-500">
                                View details
                            </a>
                        </div>
                    </div>
                </div>

                {/* Total Products Card */}
                <div className="bg-white overflow-hidden shadow rounded-lg">
                    <div className="p-5">
                        <div className="flex items-center">
                            <div className="flex-shrink-0">
                                <CubeIcon className="h-6 w-6 text-gray-400" />
                            </div>
                            <div className="ml-5 w-0 flex-1">
                                <dl>
                                    <dt className="text-sm font-medium text-gray-500 truncate">Active Products</dt>
                                    <dd>
                                        <div className="text-lg font-medium text-gray-900">
                                            {dashboardData?.production_statistics?.active_products || 0}
                                        </div>
                                    </dd>
                                </dl>
                            </div>
                        </div>
                    </div>
                    <div className="bg-gray-50 px-5 py-3">
                        <div className="text-sm">
                            <a href="/products" className="font-medium text-primary-600 hover:text-primary-500">
                                View all
                            </a>
                        </div>
                    </div>
                </div>
            </div>

            {/* Financial Summary */}
            <FinancialSummary />

            <AIInsights />

            {/* Charts Section */}
            <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
                {/* Monthly Orders Chart */}
                <div className="bg-white p-5 rounded-lg shadow">
                    <h2 className="text-lg font-medium text-gray-900 mb-4">Monthly Orders</h2>
                    <div className="h-80">
                        <ResponsiveContainer width="100%" height="100%">
                            <BarChart
                                data={dashboardData?.order_statistics?.monthly_orders || []}
                                margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                            >
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis
                                    dataKey="month"
                                    tickFormatter={(value) => {
                                        const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
                                        return monthNames[value - 1];
                                    }}
                                />
                                <YAxis />
                                <Tooltip
                                    formatter={(value) => [value, 'Orders']}
                                    labelFormatter={(value) => {
                                        const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
                                        return `${monthNames[value - 1]} ${dashboardData?.order_statistics?.monthly_orders[0]?.year}`;
                                    }}
                                />
                                <Legend />
                                <Bar dataKey="total" fill="#0ea5e9" name="Orders" />
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                </div>

                {/* Monthly Production Chart */}
                <div className="bg-white p-5 rounded-lg shadow">
                    <h2 className="text-lg font-medium text-gray-900 mb-4">Monthly Production</h2>
                    <div className="h-80">
                        <ResponsiveContainer width="100%" height="100%">
                            <LineChart
                                data={dashboardData?.production_statistics?.monthly_production || []}
                                margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                            >
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis
                                    dataKey="month"
                                    tickFormatter={(value) => {
                                        const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
                                        return monthNames[value - 1];
                                    }}
                                />
                                <YAxis
                                    domain={yAxisDomain}
                                    tickFormatter={(value) => Math.round(value)}
                                />
                                <Tooltip
                                    formatter={(value) => [value, 'Units']}
                                    labelFormatter={(value) => {
                                        const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
                                        return `${monthNames[value - 1]} ${dashboardData?.production_statistics?.monthly_production[0]?.year}`;
                                    }}
                                />
                                <Legend />
                                <Line type="monotone" dataKey="total" stroke="#0ea5e9" name="Units Produced" />
                            </LineChart>
                        </ResponsiveContainer>
                    </div>
                </div>
            </div>

            {/* Inventory Warnings Section with Tabs */}
            <div className="bg-white shadow rounded-lg overflow-hidden">
                <div className="flex justify-between items-center p-5 pb-0">
                    <h2 className="text-lg font-medium text-gray-900">Inventory Warnings</h2>
                    <div className="text-sm text-gray-500">
                        {dashboardData?.low_stock_warnings?.summary && (
                            <span>
                                {fulfillablePercentage}% Fulfillable ({fulfillableOrders} of {totalOrders} orders)
                            </span>
                        )}
                    </div>
                </div>

                <Tab.Group selectedIndex={selectedTab} onChange={setSelectedTab}>
                    <Tab.List className="flex bg-gray-50 p-1 mx-5 mt-4 space-x-1 border-b border-gray-200 rounded-t-md">
                        <Tab as={Fragment}>
                            {({ selected }) => (
                                <button
                                    className={`
                                    w-full py-2.5 text-sm leading-5 font-medium rounded-md
                                    focus:outline-none focus:ring-0
                                    ${selected
                                      ? 'bg-white text-primary-700 shadow'
                                      : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'}
                                  `}
                                >
                                  <span className="flex items-center justify-center">
                                    <TableCellsIcon className="w-5 h-5 mr-2" />
                                    Low Stock Parts
                                  </span>
                                </button>
                            )}
                        </Tab>
                        <Tab as={Fragment}>
                            {({ selected }) => (
                                <button
                                    className={`
                                    w-full py-2.5 text-sm leading-5 font-medium rounded-md
                                    focus:outline-none focus:ring-0
                                    ${selected
                                      ? 'bg-white text-primary-700 shadow'
                                      : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'}
                                  `}
                                >
                                  <span className="flex items-center justify-center">
                                    <CubeIcon className="w-5 h-5 mr-2" />
                                    Unfulfillable Products
                                  </span>
                                </button>
                            )}
                        </Tab>
                        <Tab as={Fragment}>
                            {({ selected }) => (
                                <button
                                    className={`
                                    w-full py-2.5 text-sm leading-5 font-medium rounded-md
                                    focus:outline-none focus:ring-0
                                    ${selected
                                      ? 'bg-white text-primary-700 shadow'
                                      : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'}
                                  `}
                                >
                                  <span className="flex items-center justify-center">
                                    <DocumentIcon className="w-5 h-5 mr-2" />
                                    Unfulfillable Orders
                                  </span>
                                </button>
                            )}
                        </Tab>
                        <Tab as={Fragment}>
                            {({ selected }) => (
                                <button
                                    className={`
                                    w-full py-2.5 text-sm leading-5 font-medium rounded-md
                                    focus:outline-none focus:ring-0
                                    ${selected
                                      ? 'bg-white text-primary-700 shadow'
                                      : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'}
                                  `}
                                >
                                  <span className="flex items-center justify-center">
                                    <CubeIcon className="w-5 h-5 mr-2 text-green-500" />
                                    Fulfillable Products
                                  </span>
                                </button>
                            )}
                        </Tab>
                        <Tab as={Fragment}>
                            {({ selected }) => (
                                <button
                                    className={`
                                    w-full py-2.5 text-sm leading-5 font-medium rounded-md
                                    focus:outline-none focus:ring-0
                                    ${selected
                                      ? 'bg-white text-primary-700 shadow'
                                      : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'}
                                  `}
                                >
                                  <span className="flex items-center justify-center">
                                    <CheckIcon className="w-5 h-5 mr-2 text-green-500" />
                                    Fulfillable Orders
                                  </span>
                                </button>
                            )}
                        </Tab>
                    </Tab.List>

                    <Tab.Panels>
                        {/* Low Stock Parts Panel */}
                        <Tab.Panel>
                            <LowStockPartsPanel
                                lowStockParts={dashboardData?.low_stock_warnings?.low_stock_parts || []}
                            />
                        </Tab.Panel>

                        {/* Unfulfillable Products Panel */}
                        <Tab.Panel>
                            <UnfulfillableProductsPanel
                                unfulfillableProductStats={dashboardData?.low_stock_warnings?.unfulfillable_product_stats || {}}
                            />
                        </Tab.Panel>

                        {/* Unfulfillable Orders Panel */}
                        <Tab.Panel>
                            <UnfulfillableOrdersPanel
                                unfulfillableOrders={dashboardData?.low_stock_warnings?.unfulfillable_orders || []}
                            />
                        </Tab.Panel>

                        {/* Fulfillable Products Panel */}
                        <Tab.Panel>
                            <FulfillableProductsPanel
                                allProductStats={dashboardData?.low_stock_warnings?.all_product_stats || {}}
                            />
                        </Tab.Panel>

                        {/* Fulfillable Orders Panel */}
                        <Tab.Panel>
                            <FulfillableOrdersPanel
                                fulfillableOrders={dashboardData?.low_stock_warnings?.fulfillable_orders || []}
                            />
                        </Tab.Panel>
                    </Tab.Panels>
                </Tab.Group>
            </div>
        </div>
    );
};

export default Dashboard;
